/**
 * Module that serves as a wizard for creating and updating KBs
 */
define([
  "jquery",
  "marionette",
  "messaging/messenger",
  "./kbWizardModalView",
  "./kbWizardStep/kbWizardStepModule",
  "./kbSettings/kbSettingsModule",
  "./agentAssistSettings/agentAssistSettingsModule",
  "./kbContexts/kbContextsModule",
  "./kbLocales/kbLocalesModule",
  "./kbTouchPoints/kbTouchPointsModule",
  "./kbReview/kbReviewModule",
], function (
  $,
  Marionette,
  Messenger,
  KbWizardModalView,
  KbWizardStepModule,
  KbSettingsModule,
  AgentAssistSettingsModule,
  KbContextsModule,
  KbLocalesModule,
  KbTouchPointsModule,
  KbReviewModule,
) {
  "use strict";

  return Marionette.Controller.extend({
    initialize: function (options) {
      this.name = "kbWizardModal";
      this.region = options.region;
      this.editMode = options.editMode;
      this._setUpVents();
    },
    show: function (bundle) {
      const secretKeys = Messenger.reqres.request("kb:secretKeys:list");
      this.kbStep = bundle.kbStep;
      this.kb = bundle.item;
      secretKeys.onSynced(function () {
        this.secretKey = secretKeys.findWhere({ kbId: this.kb.get("kbId") });
      }, this);
      this.layout = new KbWizardModalView.kbWizardModalLayout();
      this.kbWizardStep = this.addSubModule(
        new KbWizardStepModule({
          region: this.layout.kbWizardStep,
          editMode: this.editMode,
        }),
      );
      this.kbSettings = this.addSubModule(
        new KbSettingsModule({
          region: this.layout.kbWizardPane,
          editMode: this.editMode,
          secretKey: this.secretKey,
        }),
      );
      this.agentAssistSettings = this.addSubModule(
        new AgentAssistSettingsModule({
          region: this.layout.kbWizardPane,
          editMode: this.editMode,
        }),
      );
      this.kbContexts = this.addSubModule(
        new KbContextsModule({ region: this.layout.kbWizardPane, editMode: this.editMode }),
      );
      this.kbLocales = this.addSubModule(
        new KbLocalesModule({ region: this.layout.kbWizardPane, editMode: this.editMode }),
      );
      this.kbTouchPoints = this.addSubModule(
        new KbTouchPointsModule({ region: this.layout.kbWizardPane, editMode: this.editMode }),
      );
      this.kbReview = this.addSubModule(new KbReviewModule({ region: this.layout.kbWizardPane }));

      this.layout.on(
        "done",
        function () {
          const _this = this;
          if (this.openModule.isValid()) {
            if (Messenger.reqres.request("save:textbox")) {
              const kb = this.openModule.getKb();
              kb.on(
                "invalid",
                function (model, error) {
                  // Agent Assist invalid error check
                  if (
                    error["pcasURL"] ||
                    error["company"] ||
                    error["pcasUsername"] ||
                    error["pcasPassword"]
                  ) {
                    Messenger.okDialog("admin.kbAdmin.agentAssistUpdateError");
                    kb.off("invalid");
                  }
                },
                this,
              );
              kb.saveModel({
                success: function () {
                  _this.kb = kb;
                  _this.kb.dirty = false;
                  Messenger.vent.trigger("close:modal");
                  // If save successful and Agent assist not checked, clear out values in kb;
                  if (!kb.get("agentAssist")) {
                    _this._clearAgentAssistSettings(kb);
                  }
                  if (!_this.editMode) {
                    _this._setUpNewKb();
                  } else if (
                    _this.openModule === _this.kbSettings ||
                    _this.openModule === _this.agentAssistSettings
                  ) {
                    Messenger.vent.trigger("refresh:kb:profile", kb);
                  } else {
                    Messenger.vent.trigger("refresh:kb:response:settings", kb);
                  }
                },
                error: function (model, response) {
                  if (response.status === 400) {
                    if (_this.editMode === true) {
                      Messenger.okDialog(
                        $.t("admin.kbAdmin.updateError", { message: response.responseJSON.value }),
                      );
                    } else {
                      Messenger.okDialog(
                        $.t("admin.kbAdmin.addError", { message: response.responseJSON.value }),
                      );
                    }
                  } else {
                    Messenger.showErrorFromResponse(response);
                  }
                },
              });
            }
          } else {
            if (
              this.openModule === this.kbTouchPoints ||
              this.openModule === this.kbContexts ||
              this.openModule === this.kbLocales
            ) {
              //No need to update the KB model itself when modifying the contexts, touchPoints, or locales
              Messenger.vent.trigger("close:modal");
              //Refresh the response settings to show any contexts, touchPoints, or locales added
              Messenger.vent.trigger("refresh:kb:response:settings", this.kb);
            }
          }
        },
        this,
      );

      this.layout.on(
        "back",
        function () {
          if (this.openModule === this.agentAssistSettings) {
            this.agentAssistSettings.updateFields();
            Messenger.vent.trigger("edit:kb:settings", this.openModule.getKb());
          }
          if (this.openModule === this.kbContexts) {
            if (this.openModule.getKb().get("agentAssist")) {
              Messenger.vent.trigger("edit:agent:assist:settings", this.openModule.getKb());
            } else {
              Messenger.vent.trigger("edit:kb:settings", this.openModule.getKb());
            }
          }
          if (this.openModule === this.kbTouchPoints) {
            Messenger.vent.trigger("edit:kb:contexts", this.openModule.getKb());
          }
          if (this.openModule === this.kbLocales) {
            Messenger.vent.trigger("edit:kb:touchPoints", this.openModule.getKb());
          }
          if (this.openModule === this.kbReview) {
            Messenger.vent.trigger("edit:kb:locales", this.openModule.getKb());
          }
        },
        this,
      );

      this.layout.on(
        "next",
        function () {
          if (this.openModule.isValid()) {
            if (this.openModule === this.kbSettings) {
              this.kbSettings.updateFields();
              if (this.openModule.getKb().get("agentAssist")) {
                Messenger.vent.trigger("edit:agent:assist:settings", this.openModule.getKb());
              } else {
                Messenger.vent.trigger("edit:kb:contexts", this.openModule.getKb());
              }
            } else if (this.openModule === this.agentAssistSettings) {
              this.agentAssistSettings.updateFields();
              Messenger.vent.trigger("edit:kb:contexts", this.openModule.getKb());
            } else if (this.openModule === this.kbContexts) {
              Messenger.vent.trigger("edit:kb:touchPoints", this.openModule.getKb());
            } else if (this.openModule === this.kbTouchPoints) {
              Messenger.vent.trigger("edit:kb:locales", this.openModule.getKb());
            } else if (this.openModule === this.kbLocales) {
              Messenger.vent.trigger("show:kb:review", this.openModule.getKb());
            }
          }
        },
        this,
      );

      this.layout.on(
        "cancel",
        function () {
          if (this.kb.isNew()) {
            this.kb.deleteModel();
          }
          Messenger.vent.trigger("close:modal");
        },
        this,
      );

      this.layout.on(
        "show",
        function () {
          this._showSubViews();
        },
        this,
      );

      this.showRegion(this.layout);
    },
    _showSubViews: function () {
      this.kbWizardStep.show();
      if (this.editMode) {
        //clone the kb so that we can cancel any changes that are made in the modal
        const kbClone = this.kb.clone();
        if (this.kbStep === "settings") {
          Messenger.vent.trigger("edit:kb:settings", kbClone);
        } else if (this.kbStep === "agentAssist") {
          Messenger.vent.trigger("edit:agent:assist:settings", kbClone);
        } else if (this.kbStep === "contexts") {
          Messenger.vent.trigger("edit:kb:contexts", kbClone);
        } else if (this.kbStep === "locales") {
          Messenger.vent.trigger("edit:kb:locales", kbClone);
        } else if (this.kbStep === "touchPoints") {
          Messenger.vent.trigger("edit:kb:touchPoints", kbClone);
        }
      } else {
        this._showBeginningPage(this.kb);
      }
    },

    _clearAgentAssistSettings: function (kb) {
      kb.set("pcasURL", "");
      kb.set("company", "");
      kb.set("pcasUsername", "");
      kb.set("pcasPassword", "");
      kb.set("pcasUserAgent", "");
    },

    _showBeginningPage: function (kb) {
      this.openModule = this.kbSettings;
      this.kbSettings.show(kb);
      this.kbWizardStep.setActiveStep(1);
      this.kbWizardStep.setAgentAgent(kb.get("agentAssist"));
      this.layout.hideBackButton();
      this.layout.showNextButton();
      this.layout.hideDoneButton();
      this.layout.showCancelButton();
    },
    _setUpNewKb: function () {
      //Post KB creation steps go here
      //Save the KB defaults and them to the current user so they can access the new KB
      Messenger.vent.trigger("add:kb:defaults", this.kb.get("kbId"));
      Messenger.command.execute("add:user:kb:defaults", this.kb);
      //Add the new KB to the list managed in the kbMenuModule and then set it as active
      Messenger.command.execute("add:new:kb", this.kb);
      Messenger.command.execute("set:active:kb", this.kb.get("kbId"));
      //Go to the profile page of the new KB
      Messenger.vent.trigger("show:admin:kbProfile", { kb: this.kb });
    },
    _setUpVents: function () {
      Messenger.vent.on(
        "edit:kb:settings",
        function (kb) {
          this.openModule = this.kbSettings;
          this.kbSettings.show(kb);
          this.kbWizardStep.setActiveStep(1);
          this.kbWizardStep.setAgentAgent(kb.get("agentAssist"));
          this.layout.hideBackButton();
          if (this.editMode) {
            this.openModule.setNavigationButtons();
            if (kb.get("agentAssist")) {
              this.layout.showNextButton();
            }
            this.layout.showDoneButton();
          } else {
            this.layout.showNextButton();
            this.layout.hideDoneButton();
          }
          this.layout.showCancelButton();
        },
        this,
      );

      Messenger.vent.on(
        "edit:agent:assist:settings",
        function (kb) {
          this.openModule = this.agentAssistSettings;
          this.agentAssistSettings.show(kb);
          this.kbWizardStep.setActiveStep(2);
          this.layout.showBackButton();
          if (this.editMode) {
            this.layout.hideNextButton();
            this.layout.showDoneButton();
            this.layout.toggleDoneButton(true);
          } else {
            this.layout.showNextButton();
            this.layout.hideDoneButton();
          }
          this.layout.showCancelButton();
        },
        this,
      );

      Messenger.vent.on(
        "edit:kb:contexts",
        function (kb) {
          this.openModule = this.kbContexts;
          this.kbContexts.show(kb);
          this.kbWizardStep.setActiveStep(3);
          this.kbWizardStep.setAgentAgent(kb.get("agentAssist"));
          if (this.editMode) {
            this.layout.hideBackButton();
            this.layout.hideNextButton();
            this.layout.showDoneButton();
            this.layout.hideCancelButton();
          } else {
            this.layout.showBackButton();
            this.layout.showNextButton();
            this.layout.hideDoneButton();
            this.layout.showCancelButton();
          }
        },
        this,
      );

      Messenger.vent.on(
        "edit:kb:touchPoints",
        function (kb) {
          this.openModule = this.kbTouchPoints;
          this.kbTouchPoints.show(kb);
          this.kbWizardStep.setActiveStep(4);
          this.kbWizardStep.setAgentAgent(kb.get("agentAssist"));
          if (this.editMode) {
            this.layout.hideBackButton();
            this.layout.hideNextButton();
            this.layout.showDoneButton();
            this.layout.hideCancelButton();
          } else {
            this.layout.showBackButton();
            this.layout.showNextButton();
            this.layout.hideDoneButton();
            this.layout.showCancelButton();
          }
        },
        this,
      );

      Messenger.vent.on(
        "edit:kb:locales",
        function (kb) {
          this.openModule = this.kbLocales;
          this.kbLocales.show(kb);
          this.kbWizardStep.setActiveStep(5);
          this.kbWizardStep.setAgentAgent(kb.get("agentAssist"));
          if (this.editMode) {
            this.layout.hideBackButton();
            this.layout.hideNextButton();
            this.layout.showDoneButton();
            this.layout.hideCancelButton();
          } else {
            this.layout.showBackButton();
            this.layout.showNextButton();
            this.layout.hideDoneButton();
            this.layout.showCancelButton();
          }
        },
        this,
      );

      Messenger.vent.on(
        "show:kb:review",
        function (kb) {
          this.openModule = this.kbReview;
          this.kbReview.show(kb);
          this.kbWizardStep.setActiveStep(6);
          this.kbWizardStep.setAgentAgent(kb.get("agentAssist"));
          this.layout.showBackButton();
          this.layout.hideNextButton();
          this.layout.showDoneButton();
          this.layout.showCancelButton();
        },
        this,
      );

      Messenger.vent.on(
        "toggle:next:button",
        function (enabled) {
          this.layout.toggleNextButton(enabled);
        },
        this,
      );

      Messenger.vent.on(
        "toggle:done:button",
        function (enabled) {
          this.layout.toggleDoneButton(enabled);
        },
        this,
      );
    },
  });
});
